'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  location: string;
  rating: number;
  comment: string;
  image: string;
  alt: string;
}

interface TestimonialsSectionProps {
  className?: string;
}

const TestimonialsSection = ({ className = '' }: TestimonialsSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: 'Rajesh Kumar',
      role: 'Software Engineer',
      location: 'Bangalore',
      rating: 5,
      comment: 'ParkPing makes it easy to handle parking situations in my apartment without awkward conversations. The privacy aspect makes a real difference.',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_12c863552-1763294002377.png",
      alt: 'Professional Indian man in blue shirt smiling at camera in modern office setting'
    },
    {
      id: 2,
      name: 'Priya Sharma',
      role: 'Marketing Manager',
      location: 'Mumbai',
      rating: 5,
      comment: 'ParkPing allows me to stay reachable without sharing my phone number. It’s a simple solution that adds a strong sense of safety.',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_124644cb0-1763295764731.png",
      alt: 'Young professional woman in formal attire smiling confidently in corporate environment'
    },
    {
      id: 3,
      name: 'Amit Patel',
      role: 'Business Owner',
      location: 'Delhi',
      rating: 5,
      comment: 'We use ParkPing in our office parking to contact vehicle owners when needed. It’s straightforward, private, and works reliably.',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_14b136d25-1763296852732.png",
      alt: 'Mature businessman in grey suit with confident expression in modern workspace'
    },
    {
      id: 4,
      name: 'Sneha Reddy',
      role: 'Doctor',
      location: 'Hyderabad',
      rating: 5,
      comment: 'In time-sensitive situations, ParkPing helps others reach me without compromising privacy. It’s a thoughtful and practical solution.',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1317154cb-1766224344778.png",
      alt: 'Female doctor in white coat with stethoscope smiling warmly in hospital corridor'
    }
  ];

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isHydrated, testimonials.length]);

  const handleDotClick = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <section className={`py-20 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full border border-accent/20 mb-6">
            <Icon name="StarIcon" size={20} className="text-primary" />
            <span className="text-sm font-medium text-primary">Highly rated by early users</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-heading font-extrabold text-foreground mb-4">
            Trusted by Car Owners
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Real experiences from car owners who rely on ParkPing for private and secure parking communication.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-3xl shadow-card p-8 lg:p-12 relative overflow-hidden border border-border">
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>

            <div className="relative z-10">
              <div className="flex items-center space-x-6 mb-8">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-accent/20">
                    <AppImage
                      src={testimonials[activeIndex].image}
                      alt={testimonials[activeIndex].alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="CheckIcon" size={16} className="text-white" />
                  </div>
                </div>

                <div className="flex-1">
                  <h3 className="text-xl font-heading font-extrabold text-foreground">
                    {testimonials[activeIndex].name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {testimonials[activeIndex].role} • {testimonials[activeIndex].location}
                  </p>
                  <div className="flex items-center space-x-1 mt-2">
                    {[...Array(testimonials[activeIndex].rating)].map((_, i) => (
                      <Icon key={i} name="StarIcon" size={16} className="text-accent fill-current" />
                    ))}
                  </div>
                </div>
              </div>

              <blockquote className="text-lg text-foreground leading-relaxed mb-8">
                "{testimonials[activeIndex].comment}"
              </blockquote>

              {isHydrated && (
                <div className="flex justify-center space-x-3">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => handleDotClick(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        activeIndex === index
                          ? 'bg-accent w-8 shadow-sm shadow-accent/40'
                          : 'bg-border hover:bg-muted-foreground/40'
                      }`}
                      aria-label={`View testimonial ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 border border-accent/15 rounded-2xl mb-4 mx-auto">
              <Icon name="UserGroupIcon" size={30} className="text-primary" />
            </div>
            <div className="text-3xl font-heading font-extrabold text-foreground mb-2">50,000+</div>
            <div className="text-sm text-muted-foreground">Cards in use</div>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 border border-accent/15 rounded-2xl mb-4 mx-auto">
              <Icon name="ChatBubbleLeftRightIcon" size={30} className="text-primary" />
            </div>
            <div className="text-3xl font-heading font-extrabold text-foreground mb-2">100%</div>
            <div className="text-sm text-muted-foreground">Secure connections</div>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 border border-accent/15 rounded-2xl mb-4 mx-auto">
              <Icon name="StarIcon" size={30} className="text-primary" />
            </div>
            <div className="text-3xl font-heading font-extrabold text-foreground mb-2">5.0</div>
            <div className="text-sm text-muted-foreground">User rating</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
